import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

const NEWS_LAST_SEEN_KEY = 'news_last_seen';
const ITEMS_PER_PAGE = 4;

type NewsItem = {
  id: string;
  title: string;
  source: string | null;
  url: string;
  published_at: string | null;
  category: 'ergasiaka' | 'oikonomika' | 'dikastika' | 'nomothesia';
};

type MainTab = 'news' | 'bookmarks';

function timeAgo(iso: string | null, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return t('newsScreen.timeAgo_justNow');
  if (h < 24) return t('newsScreen.timeAgo_hours', { count: h });
  const d = Math.floor(h / 24);
  return t('newsScreen.timeAgo_days', { count: d });
}

function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export default function NewsScreen() {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { colors } = useTheme();
  const { user } = useAuth();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [items, setItems] = useState<NewsItem[]>([]);
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
  const [bookmarkedItems, setBookmarkedItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState<MainTab>('news');
  const [query, setQuery] = useState('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scrollRef = useRef<any>(null);
  const scrollX = useRef(new Animated.Value(0)).current;
  const [pageIndex, setPageIndex] = useState(0);

  // Άνοιγμα σαν εφημερίδα κατά την είσοδο στην οθόνη — ξεδιπλώνει κάθετα
  // (scaleY + ελαφριά κλίση rotateX) από πάνω, μία φορά στο mount.
  const openAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(openAnim, {
      toValue: 1,
      duration: 500,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [openAnim]);
  const openScaleY = openAnim.interpolate({ inputRange: [0, 1], outputRange: [0.12, 1] });
  const openRotateX = openAnim.interpolate({ inputRange: [0, 1], outputRange: ['-35deg', '0deg'] });
  const openOpacity = openAnim.interpolate({ inputRange: [0, 0.35, 1], outputRange: [0, 0.5, 1] });

  useFocusEffect(useCallback(() => {
    let cancelled = false;
    setLoading(true);
    void AsyncStorage.setItem(NEWS_LAST_SEEN_KEY, new Date().toISOString());

    const fetchAll = async () => {
      const [newsRes, bookmarkRes] = await Promise.all([
        supabase
          .from('news_items')
          .select('id, title, source, url, published_at, category')
          .order('published_at', { ascending: false })
          .limit(200),
        user
          ? supabase
              .from('bookmarked_news')
              .select('news_id, news_items(id, title, source, url, published_at, category)')
              .eq('user_id', user.id)
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (cancelled) return;
      if (newsRes.data) setItems(newsRes.data as NewsItem[]);

      if (bookmarkRes.data) {
        const bIds = new Set<string>();
        const bItems: NewsItem[] = [];
        for (const row of (bookmarkRes.data as unknown as { news_id: string; news_items: NewsItem | null }[])) {
          bIds.add(row.news_id);
          if (row.news_items) bItems.push(row.news_items);
        }
        setBookmarkedIds(bIds);
        setBookmarkedItems(bItems);
      }
      setLoading(false);
    };

    void fetchAll().catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]));

  async function toggleBookmark(newsId: string) {
    if (!user) return;
    const isBookmarked = bookmarkedIds.has(newsId);
    if (isBookmarked) {
      setBookmarkedIds((prev) => { const s = new Set(prev); s.delete(newsId); return s; });
      setBookmarkedItems((prev) => prev.filter((i) => i.id !== newsId));
      await supabase.from('bookmarked_news').delete().eq('user_id', user.id).eq('news_id', newsId);
    } else {
      setBookmarkedIds((prev) => new Set([...prev, newsId]));
      const item = items.find((i) => i.id === newsId);
      if (item) setBookmarkedItems((prev) => [item, ...prev]);
      await supabase.from('bookmarked_news').insert({ user_id: user.id, news_id: newsId });
    }
  }

  const q = normalize(query.trim());
  const sourceList = mainTab === 'bookmarks' ? bookmarkedItems : items;
  const filtered = useMemo(() => {
    if (!q) return sourceList;
    return sourceList.filter((i) => normalize(i.title).includes(q));
  }, [sourceList, q]);

  const pages = useMemo(() => {
    const chunks: NewsItem[][] = [];
    for (let i = 0; i < filtered.length; i += ITEMS_PER_PAGE) chunks.push(filtered.slice(i, i + ITEMS_PER_PAGE));
    return chunks;
  }, [filtered]);

  // Γύρισε στην πρώτη σελίδα κάθε φορά που αλλάζει το σύνολο αποτελεσμάτων
  // (νέα αναζήτηση, εναλλαγή tab) — αλλιώς μπορεί να μείνει «κρεμασμένος»
  // σε σελίδα που πια δεν υπάρχει.
  useEffect(() => {
    scrollRef.current?.scrollTo({ x: 0, animated: false });
    setPageIndex(0);
  }, [mainTab, q]);

  function renderCard(item: NewsItem) {
    const isBookmarked = bookmarkedIds.has(item.id);
    return (
      <Pressable
        key={item.id}
        style={({ pressed }) => [styles.newsCard, pressed && styles.newsCardPressed]}
        onPress={() => void Linking.openURL(item.url)}
        accessibilityRole="link"
      >
        <View style={styles.newsMeta}>
          {item.source ? <Text style={styles.newsSource} numberOfLines={1}>{item.source}</Text> : null}
          <Text style={styles.newsTime}>{timeAgo(item.published_at, t)}</Text>
          <Pressable
            onPress={() => void toggleBookmark(item.id)}
            hitSlop={8}
            style={styles.bookmarkBtn}
            accessibilityRole="button"
            accessibilityLabel={isBookmarked ? t('newsScreen.removeBookmark') : t('newsScreen.saveBookmark')}
          >
            <Ionicons
              name={isBookmarked ? 'bookmark' : 'bookmark-outline'}
              size={16}
              color={isBookmarked ? colors.primary : colors.textMuted}
            />
          </Pressable>
        </View>
        <Text style={styles.newsTitle}>{item.title}</Text>
        <View style={styles.newsFooter}>
          <Text style={styles.newsLink}>{t('newsScreen.openInBrowser')}</Text>
          <Ionicons name="open-outline" size={13} color={colors.textMuted} />
        </View>
      </Pressable>
    );
  }

  return (
    <Animated.View
      style={[
        styles.root,
        { paddingTop: insets.top },
        {
          opacity: openOpacity,
          transform: [
            { perspective: 1200 },
            { translateY: -screenHeight / 2 },
            { rotateX: openRotateX },
            { scaleY: openScaleY },
            { translateY: screenHeight / 2 },
          ],
        },
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} hitSlop={10} accessibilityRole="button">
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <View style={styles.headerTitle}>
          <Ionicons name="newspaper-outline" size={20} color={colors.text} />
          <Text style={styles.headerText}>{t('newsScreen.title')}</Text>
        </View>
        <View style={{ width: 24 }} />
      </View>

      {/* Main tabs */}
      <View style={styles.mainTabs}>
        {(['news', 'bookmarks'] as MainTab[]).map((tab) => (
          <Pressable
            key={tab}
            style={[styles.mainTab, mainTab === tab && styles.mainTabActive]}
            onPress={() => setMainTab(tab)}
            accessibilityRole="tab"
          >
            <Ionicons
              name={tab === 'news' ? 'newspaper-outline' : 'bookmark-outline'}
              size={14}
              color={mainTab === tab ? colors.primary : colors.textMuted}
            />
            <Text style={[styles.mainTabText, mainTab === tab && styles.mainTabTextActive]}>
              {tab === 'news' ? t('newsScreen.title') : t('newsScreen.bookmarksTab')}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* Search bar */}
      <View style={styles.searchBar}>
        <Ionicons name="search-outline" size={16} color={colors.textMuted} />
        <TextInput
          style={styles.searchInput}
          value={query}
          onChangeText={setQuery}
          placeholder={t('newsScreen.searchPlaceholder')}
          placeholderTextColor={colors.textMuted}
          returnKeyType="search"
          autoCorrect={false}
          autoCapitalize="none"
        />
        {query.length > 0 && (
          <Pressable onPress={() => setQuery('')} hitSlop={8}>
            <Ionicons name="close-circle" size={16} color={colors.textMuted} />
          </Pressable>
        )}
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : pages.length === 0 ? (
        <View style={styles.center}>
          <Ionicons
            name={mainTab === 'bookmarks' ? 'bookmark-outline' : 'newspaper-outline'}
            size={48}
            color={colors.textMuted}
          />
          <Text style={styles.emptyText}>
            {mainTab === 'bookmarks'
              ? t('newsScreen.emptyBookmarks')
              : q ? t('newsScreen.emptySearch', { query }) : t('newsScreen.emptyNews')}
          </Text>
        </View>
      ) : (
        <>
          <Animated.ScrollView
            ref={scrollRef}
            style={styles.pager}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onScroll={Animated.event(
              [{ nativeEvent: { contentOffset: { x: scrollX } } }],
              { useNativeDriver: Platform.OS !== 'web' },
            )}
            scrollEventThrottle={16}
            onMomentumScrollEnd={(e) => {
              setPageIndex(Math.round(e.nativeEvent.contentOffset.x / screenWidth));
            }}
          >
            {pages.map((pageItems, pIdx) => {
              const inputRange = [(pIdx - 1) * screenWidth, pIdx * screenWidth, (pIdx + 1) * screenWidth];
              // Το βήμα γυρίζει γύρω από την αριστερή του άκρη (σαν σελίδα βιβλίου/
              // καταλόγου) αντί να ζουμάρει στο κέντρο — translateX(-half) → rotateY
              // → translateX(+half) μετακινεί το σημείο περιστροφής στην άκρη.
              const halfWidth = screenWidth / 2;
              const rotateY = scrollX.interpolate({
                inputRange,
                outputRange: ['60deg', '0deg', '-60deg'],
                extrapolate: 'clamp',
              });
              const scale = scrollX.interpolate({ inputRange, outputRange: [0.88, 1, 0.88], extrapolate: 'clamp' });
              const opacity = scrollX.interpolate({ inputRange, outputRange: [0.35, 1, 0.35], extrapolate: 'clamp' });
              const foldShadow = scrollX.interpolate({
                inputRange,
                outputRange: [0.4, 0, 0.4],
                extrapolate: 'clamp',
              });

              return (
                <View key={pIdx} style={{ width: screenWidth, flex: 1 }}>
                  <Animated.View
                    style={[
                      styles.page,
                      {
                        opacity,
                        transform: [
                          { perspective: 1200 },
                          { translateX: -halfWidth },
                          { rotateY },
                          { translateX: halfWidth },
                          { scale },
                        ],
                      },
                    ]}
                  >
                    <Animated.View pointerEvents="none" style={[styles.foldShadow, { opacity: foldShadow }]} />
                    <View style={styles.pageMasthead}>
                      <Text style={styles.pageMastheadText}>{t('newsScreen.title')}</Text>
                      <Text style={styles.pageNumber}>{t('newsScreen.pageOf', { page: pIdx + 1, total: pages.length })}</Text>
                    </View>
                    {pageItems.map((item, i) => (
                      <View key={item.id}>
                        {renderCard(item)}
                        {i < pageItems.length - 1 && <View style={styles.separator} />}
                      </View>
                    ))}
                  </Animated.View>
                </View>
              );
            })}
          </Animated.ScrollView>

          {pages.length > 1 && (
            <View style={styles.pageDots}>
              {pages.map((_, i) => (
                <View key={i} style={[styles.dot, i === pageIndex && styles.dotActive]} />
              ))}
            </View>
          )}
        </>
      )}
    </Animated.View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    root: { flex: 1, backgroundColor: colors.background },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    headerTitle: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    headerText: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    mainTabs: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    mainTab: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: spacing.sm,
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
    },
    mainTabActive: { borderBottomColor: colors.primary },
    mainTabText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    mainTabTextActive: { color: colors.primary },
    searchBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      marginHorizontal: spacing.md,
      marginVertical: spacing.sm,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      paddingHorizontal: spacing.sm,
      paddingVertical: 8,
      borderWidth: 1,
      borderColor: colors.border,
    },
    searchInput: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.sm,
      padding: 0,
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.md,
      padding: spacing.xl,
    },
    emptyText: { color: colors.textMuted, fontSize: fontSize.md, textAlign: 'center' },
    pager: { flex: 1 },
    page: { flex: 1, paddingHorizontal: spacing.md },
    foldShadow: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: '#000',
      zIndex: 1,
    },
    pageMasthead: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      borderBottomWidth: 2,
      borderBottomColor: colors.text,
      paddingBottom: spacing.sm,
      marginBottom: spacing.sm,
    },
    pageMastheadText: {
      color: colors.text,
      fontSize: fontSize.xl,
      fontWeight: '800',
      letterSpacing: 0.5,
    },
    pageNumber: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '600' },
    separator: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: colors.border,
      marginVertical: spacing.sm,
    },
    newsCard: { gap: 6, paddingVertical: spacing.xs },
    newsCardPressed: { opacity: 0.6 },
    newsMeta: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    newsSource: {
      color: colors.primary,
      fontSize: fontSize.xs,
      fontWeight: '600',
      flexShrink: 1,
    },
    newsTime: { color: colors.textMuted, fontSize: fontSize.xs },
    bookmarkBtn: { marginLeft: 'auto' },
    newsTitle: { color: colors.text, fontSize: fontSize.md, lineHeight: 22 },
    newsFooter: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    newsLink: { color: colors.textMuted, fontSize: fontSize.xs },
    pageDots: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 6,
      paddingVertical: spacing.md,
    },
    dot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: colors.border,
    },
    dotActive: {
      width: 16,
      backgroundColor: colors.primary,
    },
  });
}
